import { Client as ConversationsClient } from "@twilio/conversations";

import { incrementUnreadSms } from "Services/state/conversations/UnreadConversationsSlice";
import { deserializeConversationMessage } from "Services/state/conversations/utils";
import { reduxStore } from "Services/state/Storage";
import { getTwilioErrorDetails, isMissingTwilioSyncResourceError } from "Types/TwilioError";

import { authenticateConversationsUser } from "./ConversationsApiService";
import { getLogger, renderErrorMessage } from "./LoggingService";
import {
    removeConversation,
    setConversation,
    updateConversation,
    setReservationMessages,
    addConversationMessage,
} from "./state/conversations/ConversationsStateProvider";

import type { Conversation, Message } from "@twilio/conversations";

const logger = getLogger("Twilio Conversations");

export let conversationsClient: ConversationsClient;

/* HELPERS */
const logConversationsFailure = (message: string, error: unknown, extraData: Record<string, unknown> = {}) => {
    const { status, code } = getTwilioErrorDetails(error);
    const context = {
        ...extraData,
        error,
        twilioStatus: status,
        twilioCode: code,
        connectionState: conversationsClient?.connectionState,
    };

    if (isMissingTwilioSyncResourceError(error)) {
        logger.warn(message, context);
        return;
    }

    logger.error(message, context);
};

const setConversationForBind = (conversation: Conversation) => {
    if (!!conversation) {
        reduxStore.dispatch(setConversation({ conversationInstance: conversation }));
    }
};
const removeConversationForLeave = (conversation: Conversation) => {
    logger.log("Left conversation", { conversationSid: conversation?.sid });
    reduxStore.dispatch(removeConversation({ conversationInstance: conversation }));
};

// If an customer initiated a conversation before the agent joined
// we need to retrieve the messages from the reservation
// and add them to the conversation so the agent has all the context
// once the agent joins the conversation the on messageAdded event will fire
// for all new messages
const handleReservationMessages = async (conversation: Conversation) => {
    try {
        /**
         * @remarks : the getMessages method does not seem to be documented properly
         *  This is how we get the the 5 most recent messages from the reservation
         */
        // get up to 5 messages from the reservation
        const message = await conversation.getMessages(30, 5, "backwards");
        if (message?.items?.length > 0) {
            // we need to deserialize the message before we can add it to the state
            const cleanItems = message.items.map((m: any) => {
                return deserializeConversationMessage(m);
            });
            reduxStore.dispatch(setReservationMessages({ sid: conversation.sid, messages: cleanItems }));
        }
        // update type to unknown and use normalizeError
    } catch (error) {
        logConversationsFailure("TwilioConversationsService error getting reservation messages", error, {
            conversation: conversation.sid,
            conversationFriendlyName: conversation.friendlyName,
        });
    }
};

const handleMessagesAdded = (m: Message) => {
    // we need to deserialize the message before we can add it to the state
    const message = deserializeConversationMessage(m);
    reduxStore.dispatch(addConversationMessage({ sid: m.conversation.sid, message }));
    // currently we only get attributes on outbound messages
    // so this is how we need to do our inbound check
    // @ts-expect-error direction is on attributes
    const direction = message.attributes?.direction ?? "inbound";
    if (direction.toLowerCase() === "inbound") {
        reduxStore.dispatch(incrementUnreadSms({ conversationSid: m.conversation.sid, smsMessageSid: m.sid }));
    }
};

async function getConversationToken(): Promise<string> {
    const { token } = await authenticateConversationsUser();
    return token;
}

/* INITIALIZATION FLOW */
// function useConversationsListeners() {
const initializeConversationsListeners = () => {
    conversationsClient.on("conversationJoined", (conversation: Conversation) => {
        logger.log("Joined conversation", { conversationSid: conversation?.sid });
        conversation?.on("updated", (updateResult: any) => {
            logger.log("Conversation was updated", {
                conversationSid: updateResult.conversation.sid,
                attributes: updateResult.conversation.attributes,
            });

            const { conversation } = updateResult;

            reduxStore.dispatch(updateConversation({ conversationInstance: conversation }));
        });

        // all new messages will be added to the conversation from here
        conversation?.on("messageAdded", handleMessagesAdded);
        setConversationForBind(conversation);
        handleReservationMessages(conversation);
    });

    conversationsClient.on("conversationLeft", removeConversationForLeave);
};

const resyncSubscribedConversations = async () => {
    try {
        const fetchedConversations = await conversationsClient.getSubscribedConversations();
        fetchedConversations.items.forEach((conversation: Conversation) => {
            if (conversation.status === "joined") {
                logger.log("Found a previously joined conversation", { conversationSid: conversation?.sid });
                setConversationForBind(conversation);
            }
        });
    } catch (error) {
        logConversationsFailure("TwilioConversationsService failed to resync subscribed conversations", error);
    }
};

const initializeStateListeners = () => {
    conversationsClient.on("connectionStateChanged", (state) => {
        logger.log(`Connection state changed: ${state}`);
        if (state === "connected") {
            logger.log("Reconnected to ConversationClient and Resyncing tasks");
            void resyncSubscribedConversations();
        }
    });
};

const listenToTokenExpiration = () => {
    conversationsClient.on("tokenAboutToExpire", async () => {
        logger.log("Conversations token about to expire");
        try {
            const token = await getConversationToken();
            await conversationsClient.updateToken(token);
            logger.log("token renewed");
        } catch (error) {
            logConversationsFailure("TwilioConversationsService failed to renew the conversations token", error);
        }
    });

    conversationsClient.on("tokenExpired", async () => {
        logger.log("Conversations token expired.");
        await initializeConversations();
        logger.log("Conversations library reinitialized!");
    });
};

const showConversationsError = (error: Error | unknown) => {
    const normalizedError =
        error instanceof Error
            ? error
            : new Error(
                  (error as { message?: string } | undefined)?.message ||
                      "TwilioConversations -> initializeConversations"
              );

    renderErrorMessage({
        content: "Unable to connect to SMS/chat. Please refresh the page and try again.",
        logAsWarn: true,
        error: normalizedError,
        logger,
    });
};

const listenToErrors = () => {
    conversationsClient.on("initFailed", ({ error }) => showConversationsError(error));
};

export const initializeConversations = async (): Promise<ConversationsClient | undefined> => {
    logger.log("initializeConversations twilio conversations", {
        convo: Boolean(conversationsClient),
    });
    if (["connecting", "connected"].includes(conversationsClient?.connectionState)) {
        logger.log("ConversationsClient skip initialization", {
            connectionState: conversationsClient?.connectionState,
        });
        return;
    }
    try {
        const token = await getConversationToken();
        if (!token) {
            logger.error("Token could not be obtained. Check configuration");
            return;
        }
        conversationsClient?.shutdown();
        conversationsClient = new ConversationsClient(token);
        listenToErrors();
        initializeStateListeners();
        initializeConversationsListeners();
        listenToTokenExpiration();
        return conversationsClient; // we just return to make testing easier
        // update type to unknown and use normalizeError
    } catch (error) {
        showConversationsError(error);
    }
};
